"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bell, Search, Grid3x3, X, Menu } from "lucide-react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "./theme-toggle";
import { navigationSections } from "./sidebar";
import { UserNav } from "./UserNav";
import { SearchCommandPalette } from "./SearchCommandPalette";

export function Header({
  onMobileMenuToggle,
}: {
  onMobileMenuToggle?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Detect OS for keyboard shortcut display (reserved for future use)
  // const isOSX = typeof navigator !== "undefined" && navigator.userAgent.includes("Mac");
  // const commandKey = isOSX ? "⌘" : "Ctrl";

  return (
    <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="flex h-14 items-center px-4 lg:px-6 gap-4">
        {/* Left section - Mobile menu */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMobileMenuToggle}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {/* Center section - Search Bar */}
        <div className="flex-1 flex justify-center">
          <div className="hidden md:flex w-full max-w-2xl">
            <div
              className={cn(
                "relative w-full group cursor-pointer",
                "transition-all duration-200",
              )}
              onClick={() => setSearchOpen(true)}
            >
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                ref={searchRef}
                type="search"
                placeholder="Search units, tenants, leases, requests..."
                readOnly
                className={cn(
                  "w-full rounded-lg border border-input bg-background/50 pl-9 pr-12 py-2 text-sm",
                  "ring-offset-background focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-primary/50 focus-visible:ring-offset-0",
                  "hover:border-primary/50 hover:bg-background/80",
                  "transition-all duration-200 cursor-pointer",
                  "placeholder:text-xs placeholder:text-muted-foreground/60",
                )}
                onClick={() => setSearchOpen(true)}
              />

              {/* Keyboard shortcut hint - always show ⌘K */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 pointer-events-none">
                <kbd className="px-1.5 py-0.5 text-xs font-medium text-muted-foreground bg-muted rounded border border-border/50">
                  ⌘K
                </kbd>
              </div>
            </div>
          </div>

          {/* Mobile Search Icon */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Right section - Icons */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMenuOpen(!menuOpen)}
            className="relative"
            aria-label="Navigation menu"
          >
            {menuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Grid3x3 className="h-5 w-5" />
            )}
          </Button>

          {/* Dropdown Menu - grouped by section for clarity */}
          {menuOpen && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] max-h-[calc(100vh-5rem)] overflow-y-auto rounded-lg border bg-background shadow-lg z-50"
            >
              <div className="p-2">
                {navigationSections.map((section) => (
                  <div key={section.label} className="mb-2 last:mb-0">
                    <div className="mb-1 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/90">
                      {section.label}
                    </div>
                    <nav className="space-y-0.5" aria-label={section.label}>
                      {section.items.map((item) => {
                        const isActive =
                          pathname === item.href ||
                          pathname?.startsWith(item.href + "/");
                        return (
                          <Link
                            key={item.name}
                            href={item.href}
                            onClick={() => setMenuOpen(false)}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                            )}
                          >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </nav>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ThemeToggle />
          <Button variant="ghost" size="icon">
            <Bell className="h-5 w-5" />
          </Button>
          <UserNav />
        </div>
      </div>

      {/* Search Command Palette Modal */}
      {searchOpen && (
        <SearchCommandPalette
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
        />
      )}
    </header>
  );
}
